import { WORK_QUEUE_REVIEW_SOURCE } from "@/components/duplicate-review/constants";
import { SAFETY_REPORTS_DEMO_ROUTE } from '@/features/safety_reports/shared/constants/safetyReportsRoutes';
import { lazy, Suspense } from 'react'
import {
  CATALOG_SECTION_IDS,
  type CatalogSectionId,
} from '@/features/catalog_case_review/shared/constants/catalogReviewConstants'
import { createRootRoute, createRoute, createRouter, notFound, Outlet, redirect } from '@tanstack/react-router'
import { store } from '@/store/store'
import { AuthFullLayout } from '@/components/layout/AuthFullLayout'
import { FullLayout } from '@/components/layout/FullLayout'
import { ToastProvider } from '@/contexts/ToastContext'
import GlobalLoader from '@/components/GlobalLoader'
import { ToastContainer } from '@/components/ui/toast-popup'
import { Toaster } from '@/components/ui/toaster'
import { Snackbar } from '@/components/ui/snackbar'
import SnackbarContainer from '@/components/Snackbar/SnackbarContainer'
import { Toaster as SonnerToaster } from 'sonner'
import { FOLLOWUP_TAKE_IN_ROUTE } from '@/features/case_follow_up/shared/constants/followupRoutes'
import { SOURCE_DOCUMENT_ROUTE } from '@/features/source_document/attachment/constants/sourceDocumentRoutes'

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-full min-h-[200px]">
      <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyRoute(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  const Component = lazy(factory)
  return function LazyPage() {
    return (
      <Suspense fallback={<PageFallback />}>
        <Component />
      </Suspense>
    )
  }
}

const AboutPage = lazyRoute(() => import('@/app/about/page'))
const AeCaseEntryPage = lazyRoute(() => import('@/app/ae-case-entry/page'))
const AssignmentCenterPage = lazyRoute(() => import('@/app/assignment-center/page'))
const WorkQueuePage = lazyRoute(() => import('@/app/assignment-center/work-queue/page'))
const AuditTrailPage = lazyRoute(() => import('@/app/audit-trail/page'))
const SafetyReportsDemoPage = lazyRoute(() => import('@/app/compliance/safety-reports/page'))
const BookingPage = lazyRoute(() => import('@/app/booking/page'))
const BusinessInquiryPage = lazyRoute(() => import('@/app/business-inquiry/page'))
const CaseIntakePage = lazyRoute(() => import('@/app/case-intake/page'))
const CaseManagementPage = lazyRoute(() => import('@/app/case-management/page'))
const CaseReviewPage = lazyRoute(() => import('@/app/case-review/page'))
const CaseReviewDetailPage = lazyRoute(() => import('@/app/case-review/[caseId]/page'))
const UnifiedCaseReviewPage = lazyRoute(() => import('@/app/case-review/case/[caseId]/page'))
const ChangePasswordPage = lazyRoute(() => import('@/app/change-password/page'))
const AccessGroupsPage = lazyRoute(() => import('@/app/configuration/access-management/groups/page'))
const AccessUsersPage = lazyRoute(() => import('@/app/configuration/access-management/users/page'))
const PermissionsSetupPage = lazyRoute(() => import('@/app/configuration/access-management/permissions-setup/page'))
const PermissionsPage = lazyRoute(() => import('@/app/configuration/access-management/permissions-setup/permissions/page'))
const RolesPage = lazyRoute(() => import('@/app/configuration/access-management/permissions-setup/roles/page'))
const ScreensPage = lazyRoute(() => import('@/app/configuration/access-management/permissions-setup/screens/page'))
const CodeListPage = lazyRoute(() => import('@/app/configuration/code-list/page'))
const ConfigurationsPage = lazyRoute(() => import('@/app/configuration/configurations/page'))
const CountryPage = lazyRoute(() => import('@/app/configuration/country/page'))
const EntitlementsPage = lazyRoute(() => import('@/app/configuration/entitlements/page'))
const OrganizationsPage = lazyRoute(() => import('@/app/configuration/organizations/page'))
const ReportTypePage = lazyRoute(() => import('@/app/configuration/report-type/page'))
const ConfigStudyPage = lazyRoute(() => import('@/app/configuration/study/page'))
const WorkflowPage = lazyRoute(() => import('@/app/configuration/workflow/page'))
const CaseSchemaPage = lazyRoute(() => import('@/app/configuration/ai/case-schema/page'))
const DueDatePoliciesPage = lazyRoute(() => import('@/app/configuration/due-date-policies/page'))
const QuerySchedulePage = lazyRoute(() => import('@/app/configuration/query-schedule/page'))
const DuplicatePolicyPage = lazyRoute(() => import('@/app/configuration/duplicate-policy/page'))
const ConversationDashboardPage = lazyRoute(() => import('@/app/conversation-dashboard/page'))
const DuplicateReviewPage = lazyRoute(() => import('@/app/duplicate-review/[caseId]/page'))
const E2bR3Page = lazyRoute(() => import('@/app/e2b-r3/page'))
const FileUploadPage = lazyRoute(() => import('@/app/file-upload/page'))
const FileUploadDetailPage = lazyRoute(() => import('@/app/file-upload/[id]/page'))
const ForgotPasswordPage = lazyRoute(() => import('@/app/forgot-password/page'))
const GatewayPage = lazyRoute(() => import('@/app/gateway/page'))
const IntakeTriagePage = lazyRoute(() => import('@/app/intake-triage/page'))
const IntakeTriageDetailPage = lazyRoute(() => import('@/app/intake-triage/[itemId]/page'))
const FollowupTakeInRoutePage = lazyRoute(() => import('@/app/follow-up-take-in/[caseId]/page'))
const SourceDocumentRoutePage = lazyRoute(() => import('@/app/source-document/[attachmentId]/page'))
const KnowledgeGraphPage = lazyRoute(() => import('@/app/knowledge-graph/page'))
const LineListingPage = lazyRoute(() => import('@/app/line-listing/page'))
const ListPage = lazyRoute(() => import('@/app/list/page'))
const LoginPage = lazyRoute(() => import('@/app/login/page'))
const OperationsPage = lazyRoute(() => import('@/app/operations/page'))
const PqcComplaintPage = lazyRoute(() => import('@/app/pqc-complaint/page'))
const ReportsPage = lazyRoute(() => import('@/app/reports/page'))
const CiomsDemoPage = lazyRoute(() => import('@/app/reports/cioms-demo/page'))
const AuthorityDeadlinesPage = lazyRoute(() => import('@/app/reports/authority-deadlines/page'))
const ResetPage = lazyRoute(() => import('@/app/reset/page'))
const RiDashboardPage = lazyRoute(() => import('@/app/ri-dashboard/page'))
const SessionTestPage = lazyRoute(() => import('@/app/session-test/page'))
const SignupPage = lazyRoute(() => import('@/app/signup/page'))
const UnauthorizedPage = lazyRoute(() => import('@/app/unauthorized/page'))
const UsersPage = lazyRoute(() => import('@/app/users/page'))
const QueryManagerPage = lazyRoute(() => import('@/app/query-manager/page'))

function MinimalLayout() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 w-full">
      <Outlet />
    </div>
  )
}


// Root route — houses global UI chrome (toasts, snackbars)
const rootRoute = createRootRoute({
  component: () => (
    <ToastProvider>
      <GlobalLoader />
      <Outlet />
      <ToastContainer />
      <Toaster />
      <Snackbar />
      <SnackbarContainer />
      <SonnerToaster />
    </ToastProvider>
  ),
})

// ── Public layout routes ───────────────────────────────────
const minimalPublicRoute = createRoute({ getParentRoute: () => rootRoute, id: 'minimal-public', component: MinimalLayout })
const fullPublicRoute = createRoute({ getParentRoute: () => rootRoute, id: 'full-public', component: FullLayout })

// ── Auth gate: redirects to /login when not authenticated ──
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  beforeLoad: () => {
    const { auth } = store.getState() as { auth: { isAuthenticated: boolean } }
    if (!auth.isAuthenticated) throw redirect({ to: '/login' })
  },
  component: Outlet,
})

const authMinimalRoute = createRoute({ getParentRoute: () => authRoute, id: 'auth-minimal', component: MinimalLayout })
const authFullRoute = createRoute({ getParentRoute: () => authRoute, id: 'auth-full', component: AuthFullLayout })

// ── Public + minimal layout ────────────────────────────────
const loginRoute = createRoute({ getParentRoute: () => minimalPublicRoute, path: '/login', component: LoginPage })
const signupRoute = createRoute({ getParentRoute: () => minimalPublicRoute, path: '/signup', component: SignupPage })
const forgotPasswordRoute = createRoute({ getParentRoute: () => minimalPublicRoute, path: '/forgot-password', component: ForgotPasswordPage })
const resetRoute = createRoute({ getParentRoute: () => minimalPublicRoute, path: '/reset', component: ResetPage })
const caseManagementRoute = createRoute({ getParentRoute: () => minimalPublicRoute, path: '/case-management', component: CaseManagementPage })

// ── Public + full layout ───────────────────────────────────
const conversationDashboardRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/conversation-dashboard', component: ConversationDashboardPage })
const unauthorizedRoute = createRoute({ getParentRoute: () => fullPublicRoute, path: '/unauthorized', component: UnauthorizedPage })

// ── Authenticated + root index redirect ────────────────────
const indexRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/',
  beforeLoad: () => { throw redirect({ to: '/case-management' }) },
  component: () => null,
})

// ── Authenticated + minimal layout ────────────────────────
const changePasswordRoute = createRoute({ getParentRoute: () => authMinimalRoute, path: '/change-password', component: ChangePasswordPage })
// One source document in its own tab, without the app menus (opened from the Assignment Center).
const sourceDocumentRoute = createRoute({ getParentRoute: () => authMinimalRoute, path: SOURCE_DOCUMENT_ROUTE, component: SourceDocumentRoutePage })

// ── Authenticated + full layout ────────────────────────────
const aeCaseEntryRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/ae-case-entry', component: AeCaseEntryPage })
const assignmentCenterRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/assignment-center', component: AssignmentCenterPage })
const workQueueRoute = createRoute({
  getParentRoute: () => authFullRoute,
  path: '/assignment-center/work-queue',
  component: WorkQueuePage,
  // issue #1697 — the "Show team activity" toggle backs onto ?show_team=true
  // so the view survives reload and is shareable. First validateSearch in this
  // file; plain function (not zod) since the schema is one optional boolean.
  validateSearch: (search: Record<string, unknown>): { show_team?: boolean } => ({
    show_team: search.show_team === true || search.show_team === 'true' ? true : undefined,
  }),
})
const aboutRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/about', component: AboutPage })
const auditTrailRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/audit-trail', component: AuditTrailPage })
const bookingRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/booking', component: BookingPage })
const businessInquiryRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/business-inquiry', component: BusinessInquiryPage })
// Legacy /case/$id redirects to /case-review/$caseId so existing bookmarks/links keep working.
const caseDetailRoute = createRoute({
  getParentRoute: () => authFullRoute,
  path: '/case/$id',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/case-review/$caseId', params: { caseId: (params as { id: string }).id } })
  },
  component: () => null,
})
const caseIntakeRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/case-intake', component: CaseIntakePage })
const caseReviewRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/case-review', component: CaseReviewPage })
const caseReviewDetailRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/case-review/$caseId', component: CaseReviewDetailPage })
const unifiedCaseReviewRoute = createRoute({
  getParentRoute: () => authFullRoute,
  path: '/case-review/case/$caseId',
  component: UnifiedCaseReviewPage,
})

// Old parallel-review bookmarks converge on the unified review's canonical case URL.
const catalogCaseReviewSectionRoute = createRoute({
  getParentRoute: () => authFullRoute,
  path: '/catalog-case-review/$caseId/$sectionId',
  beforeLoad: ({ params }) => {
    const { caseId, sectionId } = params as { caseId: string; sectionId: string }
    if (!Object.values(CATALOG_SECTION_IDS).includes(sectionId as CatalogSectionId)) {
      throw notFound()
    }
    throw redirect({ to: '/case-review/case/$caseId', params: { caseId } })
  },
  component: () => null,
})
const catalogCaseReviewRoute = createRoute({
  getParentRoute: () => authFullRoute,
  path: '/catalog-case-review/$caseId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/case-review/case/$caseId',
      params: { caseId: (params as { caseId: string }).caseId } })
  },
  component: () => null,
})
const duplicateReviewRoute = createRoute({
  getParentRoute: () => authFullRoute,
  path: '/duplicate-review/$caseId',
  component: DuplicateReviewPage,
  validateSearch: (search: Record<string, unknown>): { from?: typeof WORK_QUEUE_REVIEW_SOURCE } => ({
    from: search.from === WORK_QUEUE_REVIEW_SOURCE ? WORK_QUEUE_REVIEW_SOURCE : undefined,
  }),
})
const e2bR3Route = createRoute({ getParentRoute: () => authFullRoute, path: '/e2b-r3', component: E2bR3Page })
const fileUploadRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/file-upload', component: FileUploadPage })
const fileUploadDetailRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/file-upload/$id', component: FileUploadDetailPage })
const gatewayRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/gateway', component: GatewayPage })
const intakeTriageRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/intake-triage', component: IntakeTriagePage })
const intakeTriageDetailRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/intake-triage/$itemId', component: IntakeTriageDetailPage })
const followupTakeInRoute = createRoute({ getParentRoute: () => authFullRoute, path: FOLLOWUP_TAKE_IN_ROUTE, component: FollowupTakeInRoutePage })
const knowledgeGraphRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/knowledge-graph', component: KnowledgeGraphPage })
const lineListingRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/line-listing', component: LineListingPage })
const listRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/list', component: ListPage })
const operationsRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/operations', component: OperationsPage })
const pqcComplaintRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/pqc-complaint', component: PqcComplaintPage })
const reportsRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/reports', component: ReportsPage })
const safetyReportsDemoRoute = createRoute({ getParentRoute: () => authFullRoute, path: SAFETY_REPORTS_DEMO_ROUTE, component: SafetyReportsDemoPage })
const ciomsDemoRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/reports/cioms-demo', component: CiomsDemoPage })
const authorityDeadlinesRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/reports/authority-deadlines', component: AuthorityDeadlinesPage })
const riDashboardRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/ri-dashboard', component: RiDashboardPage })
const sessionTestRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/session-test', component: SessionTestPage })
const usersRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/users', component: UsersPage })
const accessGroupsRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/access-management/groups', component: AccessGroupsPage })
const accessUsersRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/access-management/users', component: AccessUsersPage })
const permissionsSetupRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/access-management/permissions-setup', component: PermissionsSetupPage })
const permissionsRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/access-management/permissions-setup/permissions', component: PermissionsPage })
const rolesRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/access-management/permissions-setup/roles', component: RolesPage })
const screensRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/access-management/permissions-setup/screens', component: ScreensPage })
const codeListRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/code-list', component: CodeListPage })
const configurationsRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/configurations', component: ConfigurationsPage })
const countryRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/country', component: CountryPage })
const entitlementsRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/entitlements', component: EntitlementsPage })
const organizationsRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/organizations', component: OrganizationsPage })
const reportTypeRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/report-type', component: ReportTypePage })
const configStudyRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/study', component: ConfigStudyPage })
const workflowRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/workflow', component: WorkflowPage })
const caseSchemaRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/ai/case-schema', component: CaseSchemaPage })
const dueDatePoliciesRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/due-date-policies', component: DueDatePoliciesPage })
const queryScheduleRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/query-schedule', component: QuerySchedulePage })
const duplicatePolicyRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/configuration/duplicate-policy', component: DuplicatePolicyPage })
const queryManagerRoute = createRoute({ getParentRoute: () => authFullRoute, path: '/query-manager', component: QueryManagerPage })

const routeTree = rootRoute.addChildren([
  minimalPublicRoute.addChildren([
    loginRoute,
    signupRoute,
    forgotPasswordRoute,
    resetRoute,
    caseManagementRoute,
  ]),
  fullPublicRoute.addChildren([
    unauthorizedRoute,
  ]),
  authRoute.addChildren([
    indexRoute,
    authMinimalRoute.addChildren([changePasswordRoute, sourceDocumentRoute]),
    authFullRoute.addChildren([
      aeCaseEntryRoute,
      conversationDashboardRoute,
      assignmentCenterRoute,
      workQueueRoute,
      aboutRoute,
      auditTrailRoute,
      bookingRoute,
      businessInquiryRoute,
      caseDetailRoute,
      caseIntakeRoute,
      caseReviewRoute,
      caseReviewDetailRoute,
      unifiedCaseReviewRoute,
      catalogCaseReviewRoute,
      catalogCaseReviewSectionRoute,
      duplicateReviewRoute,
      e2bR3Route,
      fileUploadRoute,
      fileUploadDetailRoute,
      gatewayRoute,
      intakeTriageRoute,
      intakeTriageDetailRoute,
      followupTakeInRoute,
      knowledgeGraphRoute,
      lineListingRoute,
      listRoute,
      operationsRoute,
      pqcComplaintRoute,
      queryManagerRoute,
      reportsRoute,
      safetyReportsDemoRoute,
      ciomsDemoRoute,
      authorityDeadlinesRoute,
      riDashboardRoute,
      sessionTestRoute,
      usersRoute,
      accessGroupsRoute,
      accessUsersRoute,
      permissionsSetupRoute,
      permissionsRoute,
      rolesRoute,
      screensRoute,
      codeListRoute,
      configurationsRoute,
      countryRoute,
      entitlementsRoute,
      organizationsRoute,
      reportTypeRoute,
      configStudyRoute,
      workflowRoute,
      caseSchemaRoute,
      dueDatePoliciesRoute,
      queryScheduleRoute,
      duplicatePolicyRoute,
    ]),
  ]),
])

export const router = createRouter({ routeTree })
